import React, { useState } from "react";
import { api, formatErr } from "../lib/api";
import { Bug, X, Camera, Send, Check, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MAX_SCREENSHOT_BYTES = 800 * 1024;

/**
 * BugReportButton — a small floating "Report bug" pill anchored bottom-right.
 * Visible only for authenticated users. Submits to /api/bug-reports with an
 * optional screenshot of the current page (captured client-side via html2canvas
 * + downscaled JPEG to stay under the server's 800 KB cap).
 */
export default function BugReportButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null; // unauthenticated users don't see it

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 h-12 px-4 rounded-full bg-zinc-900 hover:bg-blue-600 text-white text-sm font-semibold inline-flex items-center gap-2 shadow-lg shadow-zinc-900/20 transition-colors"
        title="Segnala un bug"
        data-testid="bug-report-fab"
      >
        <Bug className="h-4 w-4" />
        <span className="hidden sm:inline">Segnala bug</span>
      </button>
      {open && <BugReportModal onClose={() => setOpen(false)} />}
    </>
  );
}

function BugReportModal({ onClose }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  async function captureScreenshot() {
    // Lazy-import html2canvas only when needed (~50KB)
    const html2canvas = (await import("html2canvas")).default;
    // Hide the modal during capture so the report shows the bug, not the form
    const modal = document.querySelector('[data-testid="bug-report-modal"]');
    if (modal) modal.style.visibility = "hidden";
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.6, // downscale to keep payload small
        logging: false,
        backgroundColor: "#ffffff",
        useCORS: true,
      });
      // Encode as JPEG @ 0.7 quality — much smaller than PNG
      let dataUrl = canvas.toDataURL("image/jpeg", 0.7);
      // Belt-and-suspenders: if still too large, downscale further
      if (dataUrl.length > MAX_SCREENSHOT_BYTES) {
        const c2 = document.createElement("canvas");
        c2.width = canvas.width * 0.7;
        c2.height = canvas.height * 0.7;
        const ctx = c2.getContext("2d");
        ctx.drawImage(canvas, 0, 0, c2.width, c2.height);
        dataUrl = c2.toDataURL("image/jpeg", 0.6);
      }
      return dataUrl.length <= MAX_SCREENSHOT_BYTES ? dataUrl : null;
    } finally {
      if (modal) modal.style.visibility = "";
    }
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (title.trim().length < 3) return setErr("Titolo troppo corto");
    if (description.trim().length < 10) return setErr("Descrizione troppo breve (min 10 caratteri)");
    setBusy(true);
    try {
      let screenshot_b64 = null;
      if (includeScreenshot) {
        try {
          screenshot_b64 = await captureScreenshot();
        } catch (err) {
          // Screenshot is optional — continue without it
          console.warn("Screenshot capture failed:", err);
        }
      }
      await api.post("/bug-reports", {
        title: title.trim(),
        description: description.trim(),
        url: window.location.href,
        user_agent: navigator.userAgent,
        screenshot_b64,
      });
      setDone(true);
      setTimeout(onClose, 2500);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore invio");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="bg-white rounded-3xl card-soft w-full max-w-sm p-8 text-center dark:bg-zinc-900" data-testid="bug-report-success">
          <div className="h-12 w-12 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3 dark:bg-emerald-950/40">
            <Check className="h-6 w-6" />
          </div>
          <h3 className="font-display text-xl font-medium">Grazie!</h3>
          <p className="text-sm text-zinc-600 mt-1 dark:text-zinc-400">Bug segnalato. Controlleremo al più presto.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="bug-report-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-400">Beta feedback</div>
              <h2 className="font-display text-2xl font-medium tracking-tight inline-flex items-center gap-2">
                <Bug className="h-5 w-5 text-blue-600" /> Segnala un bug
              </h2>
            </div>
            <button type="button" onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="bug-report-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Titolo *</label>
            <input
              required minLength={3} maxLength={140}
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="input-soft mt-1.5"
              placeholder="Es. La cancellazione non aggiorna l'agenda"
              data-testid="bug-report-title"
            />
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Descrizione *</label>
            <textarea
              required minLength={10} maxLength={4000} rows={5}
              value={description} onChange={(e) => setDescription(e.target.value)}
              className="input-soft mt-1.5 resize-none"
              placeholder="Cosa stavi facendo? Cosa ti aspettavi che succedesse? Cosa è successo invece?"
              data-testid="bug-report-description"
            />
            <p className="text-[10px] text-zinc-400 mt-1 dark:text-zinc-500">
              {description.length}/4000 · Più dettagli ci dai, più velocemente lo risolviamo.
            </p>
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-xs text-zinc-700 dark:text-zinc-300">
            <input type="checkbox" checked={includeScreenshot} onChange={(e) => setIncludeScreenshot(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" data-testid="bug-report-screenshot-toggle" />
            <span>
              <span className="inline-flex items-center gap-1 font-semibold"><Camera className="h-3 w-3" /> Includi screenshot della pagina</span>
              <br />
              <span className="text-zinc-500 text-[11px] dark:text-zinc-400">Aiuta a capire il contesto. Lo screenshot non contiene la tua password.</span>
            </span>
          </label>

          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 inline-flex items-center gap-2 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-800/50">
              <AlertTriangle className="h-3.5 w-3.5" /> {err}
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-accent w-full disabled:opacity-50" data-testid="bug-report-submit">
            {busy ? "Invio…" : (<><Send className="h-4 w-4 mr-2" />Invia segnalazione</>)}
          </button>

          <p className="text-[10px] text-zinc-400 text-center pt-1 dark:text-zinc-500">
            Verrà inviata insieme a: URL pagina, browser usato. Niente password o dati sensibili.
          </p>
        </form>
      </div>
    </div>
  );
}
